import { useSuspenseQuery } from '@tanstack/react-query'
import { useAuth } from '@/providers/auth-provider'
import { Profile, profile } from '@/lib/queries/profiles'
import { normalizeFullName } from '@/lib/utils'

export type CurrentProfile = {
  id: string
  email: string
  fullName: string | null
  avatarUrl: string | null
}

function getNonEmptyString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null
}

export function normalizeProfile(row: Profile): CurrentProfile {
  const email = row.email
  const fullNameCandidate = getNonEmptyString(row.full_name)
  const fullName = fullNameCandidate
    ? normalizeFullName(fullNameCandidate)
    : null
  const avatarUrl = getNonEmptyString(row.avatar_url)

  return {
    id: row.id,
    email,
    fullName,
    avatarUrl,
  }
}

/**
 * Hook to get current user profile with normalized fields.
 * Uses useSuspenseQuery - must be wrapped in Suspense boundary.
 * Only for use in authenticated contexts.
 *
 * Uses useAuth() directly instead of route context to avoid
 * TanStack Router race condition where useRouteContext() can
 * return undefined during initial render.
 *
 * AuthenticatedLayout guarantees user exists before children render.
 * Throws if no user (programming error - used outside authenticated context).
 */
export function useCurrentProfile() {
  const { user } = useAuth()

  // AuthenticatedLayout short-circuits rendering until auth is ready
  // If we get here without a user, it's a programming error
  if (!user) {
    throw new Error('useCurrentProfile must be used in authenticated context')
  }

  const { data: profileData } = useSuspenseQuery(profile.findOne(user.id))

  const normalizedProfile = normalizeProfile(profileData)

  return {
    profile: profileData,
    profileId: normalizedProfile.id,
    user,
    email: normalizedProfile.email,
    fullName: normalizedProfile.fullName,
    avatarUrl: normalizedProfile.avatarUrl,
    displayName: normalizedProfile.fullName ?? normalizedProfile.email,
    userId: user.id,
  }
}
